import { COMPATIBILITY_WILDCARD } from "../constants";

/** True when a mapping field means apply to every target. */
export const isApplyAll = (value) => value === COMPATIBILITY_WILDCARD;

/** Returns the apply-all sentinel or an empty id list for checkbox toggles. */
export const setApplyAll = (checked) => (checked ? COMPATIBILITY_WILDCARD : []);

/** Parses a comma-separated string or array into trimmed id strings. */
const parseIdList = (raw) => {
	if (Array.isArray(raw)) {
		return raw.map((v) => String(v).trim()).filter(Boolean);
	}
	if (typeof raw === "string") {
		return raw
			.split(",")
			.map((v) => v.trim())
			.filter(Boolean);
	}
	return [];
};

/** Returns concrete mapping ids, or [] when apply-all is set. */
export const getMappingIds = (value) => {
	if (isApplyAll(value)) return [];
	return Array.isArray(value) ? value : [];
};

/** Adds an id to a mapping list without duplicating apply-all or existing ids. */
export const addMappingId = (current, id) => {
	if (!id || isApplyAll(id)) return getMappingIds(current);
	const list = getMappingIds(current);
	if (list.includes(id)) return list;
	return [...list, id];
};

/** Removes an id from a mapping list, clearing apply-all to an empty list. */
export const removeMappingId = (current, id) => {
	if (isApplyAll(current)) return [];
	return getMappingIds(current).filter((value) => value !== id);
};

/** Normalizes a mapping field for create/update API payloads. */
export const normalizeMappingForPayload = (value) => {
	if (isApplyAll(value)) return COMPATIBILITY_WILDCARD;
	return parseIdList(value);
};

/**
 * Normalizes offerMapping field values from the API into draft shape:
 * "*" | string[] (IDs). Supports object maps like { id: true } and enriched { id, name }.
 */
export const normalizeMappingForDraft = (value) => {
	if (value === COMPATIBILITY_WILDCARD) return COMPATIBILITY_WILDCARD;
	if (Array.isArray(value)) {
		return value
			.map((entry) => {
				if (entry && typeof entry === "object") {
					return String(entry.id ?? "");
				}
				return String(entry ?? "");
			})
			.filter(Boolean);
	}
	if (value && typeof value === "object") {
		const keys = Object.keys(value).filter((key) => value[key]);
		if (keys.includes(COMPATIBILITY_WILDCARD)) return COMPATIBILITY_WILDCARD;
		return keys;
	}
	if (typeof value === "string") {
		return parseIdList(value);
	}
	return [];
};

/** Keeps view-model mapping values as arrays or the apply-all sentinel. */
export const normalizeMappingValue = (value) => {
	if (Array.isArray(value)) return value;
	if (value === COMPATIBILITY_WILDCARD) return COMPATIBILITY_WILDCARD;
	return value;
};

/** Builds menuLabels { [id]: name } from enriched menu mapping entries. */
export const buildMenuLabelsFromMapping = (menuIds) => {
	const labels = {};
	if (!Array.isArray(menuIds)) return labels;
	menuIds.forEach((entry) => {
		if (!entry || typeof entry !== "object") return;
		const id = String(entry.id ?? "").trim();
		const name = entry.name != null ? String(entry.name).trim() : "";
		if (id && name) {
			labels[id] = name;
		}
	});
	return labels;
};
